<template>
  <div>
    <Sidebar
      :visible.sync="localIsVisible"
      :showCloseIcon="true"
      class="sidebar-container"
      style="min-height: 95vh; overflow-y: auto; width: 255px"
      @hide="onSidebarHide"
      unstyled="false"
    >
      <div class="headerSidebar">
        <Button
          icon="pi pi-times"
          class="p-button-rounded p-button-text p-button-lg"
          @click="onSidebarHide"
          style="color: #fff; margin-right: 0.3em"
          test-id="close-sidebar"
        />
      </div>
      <div class="menuSidebar secondaryFont">
        <!-- acá se muestran los botones "top" arriba de todos los grupos -->
        <div v-for="link in nonGroupedLinks.topBtns" :key="link.pathName">
          <div v-if="link.type === 'button'">
            <Button
              @click="handleLinkClick(link)"
              class="buttons"
              style="width: 100%; margin-bottom: 15px; background-color: #0b5195; border: none"
            >
              <i :class="['pi', link.icon]"></i>{{ link.titulo }}
            </Button>
          </div>
          <div
            v-else-if="link.type === 'link'"
            @click="handleLinkClick(link)"
            class="routerLinksTitles"
            :style="{ 'margin-bottom': dynamicMargin }"
          >
            <div class="routerLinks">
              <i :class="['pi', link.icon]"></i>
              {{ link.titulo }}
            </div>
          </div>
        </div>
        <div
          v-for="group in groupedLinks"
          :key="group.name"
          class="linkGroups"
          style="margin-left: 5px"
        >
          <div class="group-title">
            <span
              class="group-title-text"
              style="color: #fff; display: flex; align-items: center"
              @click="toggleGroup(group.name)"
            >
              <i :class="['pi', group.icon]"></i>
              {{ group.name }}
            </span>
            <Button
              v-if="group.links.length > 0"
              :icon="group.isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
              class="p-button-text p-button-sm"
              @click="toggleGroup(group.name)"
              style="margin-left: auto; color: #fff"
              :test-id="`toggle-group-${group.name}`"
            />
          </div>
          <div
            v-if="group.isExpanded && group.links.length > 0"
            v-for="link in group.links"
            :key="link.pathName"
          >
            <!-- Renderizado de submenús -->
            <template v-if="link.type === 'submenu'">
              <div class="submenu">
                <div
                  class="submenu-header"
                  @click="toggleSubmenu(link.pathName)"
                  style="cursor: pointer"
                >
                  <span style="display: flex; align-items: center">
                    {{ link.titulo }}
                  </span>
                  <i
                    :class="[
                      'pi',
                      openSubmenus[link.pathName] ? 'pi-chevron-up' : 'pi-chevron-down',
                    ]"
                    style="margin-left: auto"
                  ></i>
                </div>
                <div v-if="openSubmenus[link.pathName]" class="submenu-items">
                  <div
                    v-for="item in link.items"
                    :key="item.pathName"
                    class="routerLinksTitles"
                    @click="handleLinkClick(item)"
                  >
                    <div class="routerLinks" style="display: flex; align-items: center">
                      {{ item.titulo }}
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div v-if="link.type === 'button'">
              <Button
                @click="handleLinkClick(link)"
                class="buttons"
                style="width: 100%; margin-bottom: 15px"
                >{{ link.titulo }}</Button
              >
            </div>
            <div
              v-else-if="link.type === 'link'"
              @click="handleLinkClick(link)"
              class="routerLinksTitles"
              :style="{ 'margin-bottom': dynamicMargin }"
            >
              <div class="routerLinks">
                {{ link.titulo }}
              </div>
            </div>
          </div>
        </div>

        <!-- acá se muestran los botones "bottom" debajo de todos los grupos -->
        <div v-for="link in nonGroupedLinks.bottomBtns" :key="link.pathName">
          <Button
            @click="handleLinkClick(link)"
            class="buttons"
            style="width: 100%; margin-bottom: 15px; background-color: #0b5195; border: none"
          >
            <i :class="['pi', link.icon]"></i>
            {{ link.titulo }}
          </Button>
        </div>
      </div>
    </Sidebar>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, getCurrentInstance } from "vue";
import Sidebar from "primevue/sidebar";
import { sidebarLinks, groupIcons } from "./sidebarLinks";

export default defineComponent({
  components: {
    Sidebar,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    userType: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance();
    const localIsVisible = ref(props.isVisible);
    const expandedGroups = ref({});
    const openSubmenus = ref({});
    const isDevMode = process.env.VUE_APP_VUE_MODE === "development";
    const isTesting = process.env.VUE_APP_BACKEND_ENVIRONMENT?.includes(
      "siistesting.electro3.com.ar"
    );

    const filteredLinks = computed(() => {
      return sidebarLinks.filter((link) => {
        const excludedRoles = Array.isArray(link.excludesRoles) ? link.excludesRoles : [];
        const allowedRoles = Array.isArray(link.roles) ? link.roles : [];
        const isDevOnly = !!link.devOnly;

        if (isDevOnly && !isDevMode && !isTesting) return false;

        if (excludedRoles.includes(props.userType)) return false;
        if (allowedRoles.length === 0) return true;
        return allowedRoles.includes(props.userType);
      });
    });

    const uniqueGroups = computed(() => {
      return [...new Set(filteredLinks.value.map((link) => link.grupo))];
    });

    const nonGroupedLinks = computed(() => {
      let topBtns = filteredLinks.value.filter((link) => link.grupo === "TOP");
      if (props.userType === "Notificante") {
        topBtns = topBtns.filter((link) => link.pathName !== "escritorio");
      }
      const bottomBtns = filteredLinks.value.filter((link) => link.grupo === "BOTTOM");
      return { topBtns, bottomBtns };
    });

    const groupedLinks = computed(() => {
      return uniqueGroups.value
        .filter((group) => group !== "TOP" && group !== "BOTTOM")
        .map((group) => ({
          name: group,
          icon: groupIcons[group] || "",
          links: filteredLinks.value.filter((link) => link.grupo === group),
          isExpanded: expandedGroups.value[group] || false,
        }))
        .filter((group) => group.links.length > 0 && group.name !== "");
    });

    const dynamicMargin = computed(() => {
      const minLength = 10;
      const baseMargin = "10px";
      for (const group of groupedLinks.value) {
        for (const link of group.links) {
          if (link.titulo.length > minLength) {
            return "20px";
          }
        }
      }
      return baseMargin;
    });

    function onSidebarHide() {
      localIsVisible.value = false;
      emit("update:isVisible", false);
    }

    async function handleLinkClick(link) {
      if (!instance) return;

      if (link.pathName === "departamentales") {
        window.open(
          "https://docs.google.com/spreadsheets/d/1jFAq_uItOydo0KU6DqWcXi-XNsY0OaRj539-LfwmjEQ/edit?usp=sharing"
        );
        return;
      }

      if (link.action === "logout") {
        emit("logout");
        return;
      }

      const currentRoute = instance.proxy.$route.path;
      if (link.path !== currentRoute) {
        try {
          await instance.proxy.$router.push({ path: link.path });

          instance.proxy.$store.dispatch("siis/setPersonsAndEffectDisabled", false);
          instance.proxy.$store.dispatch("siis/setAfectationCardDisabled", false);
          await instance.proxy.$store.dispatch("sumario/resetState");

          localIsVisible.value = false;
        } catch (err) {
          console.error("Hay datos sin guardar", err);
        }
      }
    }

    function toggleGroup(groupName) {
      const updated = { ...expandedGroups.value };

      updated[groupName] = !updated[groupName];

      if (updated[groupName]) {
        Object.keys(updated).forEach((key) => {
          if (key !== groupName) {
            updated[key] = false;
          }
        });
      }

      expandedGroups.value = updated;
    }

    function toggleSubmenu(subMenuName) {
      const updated = { ...openSubmenus.value };

      updated[subMenuName] = !updated[subMenuName];
      openSubmenus.value = updated;
    }

    watch(
      () => props.isVisible,
      (newVal) => {
        localIsVisible.value = newVal;
      }
    );

    return {
      localIsVisible,
      expandedGroups,
      openSubmenus,
      nonGroupedLinks,
      groupedLinks,
      dynamicMargin,
      onSidebarHide,
      handleLinkClick,
      toggleGroup,
      toggleSubmenu,
    };
  },
});
</script>
<style>
.headerSidebar {
  width: 100%;
  height: 57px;
  margin: -2px -5px 0 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.menuSidebar {
  max-height: calc(95vh - 57px);
  overflow-y: auto;
  cursor: pointer;
  padding: 7px 10px;
  margin-top: 10px;
  padding-bottom: 3.5em;
}
.sidebar-container {
  margin-top: 3.5em;
  background-color: #fff;
  width: 270px !important;
}
.routerLinks {
  color: #fff;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 5px;
  box-sizing: border-box;
  padding: 12px 14px;
  height: 35px;
}

.routerLinks:hover {
  background-color: #155faa;
}

.routerLinksTitles {
  margin-top: 10px;
}
.routerLinksTitles:hover {
  margin: 10px 0;
}

.menuSidebar i.pi {
  font-size: 20px;
  margin-right: 10px;
}
.buttons {
  color: #155faa;
  list-style: none;
  font-weight: 700 !important;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  margin-bottom: 15px;
  border: none;
  padding: 8px 16px 8px 9px;
  font-family: "Inter" !important;
}

.buttons:hover {
  background-color: #155faa !important;
}

.buttons:focus {
  outline: none;
  box-shadow: none;
}

.p-button {
  outline: none;
  box-shadow: none;
}

.p-button:focus {
  border: none;
}

.group-title {
  display: flex;
  align-items: center;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.8);
  margin-left: 10px;
}

.submenu {
  margin-bottom: 25px;
}

.submenu-header {
  color: #fff;
  background: none;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 10px 0 20px 15px;
}

.submenu-header i.pi {
  font-size: 0.875rem;
}

.submenu-items {
  margin-left: 1em;
  background: none;
}

.short-line {
  border: 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  width: 10px;
  margin-right: 5px;
}

.long-line {
  flex-grow: 1;
  border: 0;
  height: 1px;
  background: #fff;
  margin-left: 5px;
}

.group-title-text {
  position: relative;
}

.linkGroups {
  margin-bottom: 20px;
}

.p-sidebar-content {
  background-color: #0b5195 !important;
}

.p-sidebar .p-sidebar-header + .p-sidebar-content {
  padding: 1rem 0 1rem 0.5rem;
}

@media (max-width: 768px) {
  .sidebar-container {
    width: 70% !important;
  }
}
</style>
